import { useMemo } from "react";
import { useSelector } from "react-redux";

import {
  userTravelVisasSelector,
  userSocialSecurityFormsSelector,
  userDrivingPermitsSelector,
  userPassportsSelector,
  userVehiclesSelector,
  userLoyaltyProgramsSelector,
} from "Bands/state";
import {
  IUserSocialSecurityForm,
  IUserDrivingPermit,
  IUserPassport,
  IUserTravelVisa,
  USER_ACTIONS,
  IUserVehicle,
  IUserLoyaltyProgram,
} from "Bands/types";
import { usePagination } from "./usePagination";

export const useSortedUserDocuments = () => {
  const { pages: passportPages } = usePagination(USER_ACTIONS.getUserPassports);
  const passportIds = useMemo(() => passportPages?.[1], [passportPages]);

  const { pages: visaPages } = usePagination(USER_ACTIONS.getUserTravelVisas);
  const visaIds = useMemo(() => visaPages?.[1], [visaPages]);

  const { pages: formPages } = usePagination(
    USER_ACTIONS.getUserSocialSecurityForms
  );
  const formIds = useMemo(() => formPages?.[1], [formPages]);

  const { pages: permitPages } = usePagination(
    USER_ACTIONS.getUserDrivingPermits
  );
  const permitIds = useMemo(() => permitPages?.[1], [permitPages]);

  const { pages: vehiclePages } = usePagination(USER_ACTIONS.getUserVehicles);
  const vehicleIds = useMemo(() => vehiclePages?.[1], [vehiclePages]);

  const { pages: loyaltyPages } = usePagination(
    USER_ACTIONS.getUserLoyaltyPrograms
  );
  const loyaltyIds = useMemo(() => loyaltyPages?.[1], [loyaltyPages]);

  const unsortedPassports = useSelector(userPassportsSelector);
  const usortedTravelVisas = useSelector(userTravelVisasSelector);
  const unsortedSocialSecurityForms = useSelector(
    userSocialSecurityFormsSelector
  );
  const unsortedDrivingPermits = useSelector(userDrivingPermitsSelector);
  const unsortedVehicles = useSelector(userVehiclesSelector);
  const unsortedLoyaltyPograms = useSelector(userLoyaltyProgramsSelector);

  const passports = useMemo(
    () =>
      passportIds
        ?.map((path) => unsortedPassports?.find(({ id }) => path.includes(id)))
        .filter((item) => item !== undefined) as IUserPassport[],
    [passportIds, unsortedPassports]
  );

  const travelVisas = useMemo(
    () =>
      visaIds
        ?.map((path) => usortedTravelVisas?.find(({ id }) => path.includes(id)))
        .filter((item) => item !== undefined) as IUserTravelVisa[],
    [visaIds, usortedTravelVisas]
  );

  const socialSecurityForms = useMemo(
    () =>
      formIds
        ?.map((path) =>
          unsortedSocialSecurityForms?.find(({ id }) => path.includes(id))
        )
        .filter((item) => item !== undefined) as IUserSocialSecurityForm[],
    [formIds, unsortedSocialSecurityForms]
  );

  const drivingPermits = useMemo(
    () =>
      permitIds
        ?.map((path) =>
          unsortedDrivingPermits?.find(({ id }) => path.includes(id))
        )
        .filter((item) => item !== undefined) as IUserDrivingPermit[],
    [permitIds, unsortedDrivingPermits]
  );

  const vehicles = useMemo(
    () =>
      vehicleIds
        ?.map((path) => unsortedVehicles?.find(({ id }) => path.includes(id)))
        .filter((item) => item !== undefined) as IUserVehicle[],
    [vehicleIds, unsortedVehicles]
  );

  const loyaltyPrograms = useMemo(
    () =>
      loyaltyIds
        ?.map((path) =>
          unsortedLoyaltyPograms?.find(({ id }) => path.includes(id))
        )
        .filter((item) => item !== undefined) as IUserLoyaltyProgram[],
    [loyaltyIds, unsortedLoyaltyPograms]
  );

  return {
    passports,
    travelVisas,
    socialSecurityForms,
    drivingPermits,
    vehicles,
    loyaltyPrograms,
  };
};
